// Messaging i18n namespace (Module 17 §4, slice P3-B part 2). Wired into
// apps/web's aggregated `dictionaries` (src/i18n-dictionaries.ts) so the CI
// key-parity gate (`pnpm run i18n:parity`, TESTING §12.1 / D-014) covers it
// automatically, and declared in src/i18n-completeness.ts so the VALUE gate
// knows which locales are real.
//
// `messagingEn` is authoritative; `MessagingDict = Translated<typeof
// messagingEn>` makes ru/zh/fr parity-checked at COMPILE time (a missing or
// extra key fails tsc). `ru` is a real translation (messaging.ru.ts) — the
// design partner's sales managers answer their customers in Russian; zh/fr
// are English placeholders, stated in i18n-completeness.ts rather than
// implied (D-122).
//
// FOUR WORDS THIS MODULE HAD TO SETTLE IN BOTH LANGUAGES AT ONCE, because a
// term settled in English and left to a later translator is not settled:
//
//   `thread`        → «переписка» — the office RECORD of the exchange, NOT
//                     «чат», which is the customer's own app surface (the
//                     Telegram chat the identity's address lives in) and
//                     keeps that meaning in the identity block.
//   `unclaimed`     → «неразобранные» — the inbox word a Russian sales office
//                     already uses for an unsorted pile; not «непривязанные»
//                     (which describes the identity, not the work).
//   `link code`     → «код привязки» — привязка is what the handshake does
//                     (binds an address to a person); not «код подключения»,
//                     which module 17 reserves for CONNECTING the bot (§4.4).
//   `session window`→ «окно диалога» — the WhatsApp 24-hour reply window; a
//                     dialogue's window, not «окно сессии» (an auth session
//                     elsewhere in the platform) and not module 04's
//                     «блокировка» family, which stays operational.
//
// Alignments with neighbours, checked rather than assumed: opt-out renders
// «отказ от рассылки» exactly as sales.consent.opted_out does (same fact,
// same words on the same customer); Telegram/WhatsApp stay the products' own
// names (sales.channel.* precedent, IDENTICAL_BY_DESIGN); «шаблон» is shared
// with the catalogue's PRODUCT templates, so every screen title here says
// «шаблоны сообщений», never bare «шаблоны».
//
// SERVER PROBLEM TITLES ARE NOT TRANSLATED (D-022): a refusal renders the
// server's own sentence, labelled by `common.serverSaid`, so the reader can
// see where the words came from. Same for the provider's own error text on
// the §4.4 connect/probe surfaces — verbatim, as §4.4 requires.
//
// ENTITY NOUNS ARE NOT IN HERE either. Customer names, template codes, bot
// usernames, addresses and template bodies are TENANT DATA; template BODIES
// in particular are the tenant's authored per-locale artifacts (§2.5), which
// is why the variant editor shows one body per locale instead of one field.

import type { Translated } from "../../../i18n-shape.js";
import { messagingRuDict } from "./messaging.ru.js";

export const messagingEn = {
  nav: {
    aria: "Messaging sections",
    inbox: "Inbox",
    templates: "Message templates",
    settings: "Settings",
  },

  common: {
    loading: "Loading…",
    retry: "Try again",
    requestId: "Request ID",
    // The label in front of the server's own words — never a paraphrase of
    // them (D-022).
    serverSaid: "Refused: {{title}}",
    close: "Close",
    cancel: "Cancel",
    save: "Save",
    saving: "Saving…",
    copied: "Copied",
    // One page at a time (spec 17 §4.1, §4.2, §4.3): the rows shown and the
    // moves to the pages either side. Newer and Older on the lists read
    // newest first; Previous and Next on the catalog, which reads by code.
    pageRange: "Showing {{from}}–{{to}}",
    pageNewer: "Newer",
    pageOlder: "Older",
    pagePrev: "Previous page",
    pageNext: "Next page",
  },

  access: {
    title: "Not available to your role",
    area: "Messaging holds nothing for your role. Ask a group admin if you need access.",
    inbox:
      "The conversation inbox is for sales and the admins; planning, finance and quality read it.",
    templates: "The message templates are for sales, planning, finance and the admins.",
    settings: "Messaging settings are for the group and facility admins.",
  },

  provider: {
    telegram: "Telegram",
    whatsapp: "WhatsApp",
    // P3-E2 PR E2-2e (D-327): the tenant's one monitored address.
    email: "Email",
  },

  offline: {
    title: "You are offline",
    body: "You're offline. This page reloads when you're back.",
  },

  // Relative ages for list rows and health lines (the home.age shape).
  age: {
    now: "now",
    min: "{{count}} min",
    h: "{{count}} h",
    d: "{{count}} d",
  },

  // ---- §4.1 conversations inbox — left pane --------------------------------
  inbox: {
    title: "Inbox",
    tabsAria: "Conversation filters",
    tabMine: "Mine",
    tabUnassigned: "Unassigned",
    tabAll: "All",
    tabSnoozed: "Snoozed",
    tabClosed: "Closed",
    tabUnclaimed: "Unclaimed",
    // §4.2 (D-327 rulings 7 and 18): held mail from known customers whose
    // sender domain did not verify, beside Unclaimed, for holders of
    // `messaging.conversation.manage` only.
    tabHeld: "Unverified mail",
    // NOT bare "Search": the topbar carries a global search and the certified
    // walkthroughs address controls by exact accessible name.
    search: "Search conversations",
    // The hint names only what `q` matches for THIS reader (#272). The phone
    // or chat-id tail matches only for a holder of `messaging.address.read`;
    // for anyone else the server leaves the address out of the search, so
    // offering them a tail would promise a match that never comes. The tail
    // names the chat ID first: Telegram is the live provider (WhatsApp is
    // parked, D-232), so every live address is a chat id, and a customer's
    // phone number never finds a Telegram thread. There is no order-number
    // leg at all (spec 17 §5), so neither hint offers one. Two keys rather
    // than one, so the screen has to pick.
    searchHintWithTail: "Customer or contact name, or the last digits of the chat ID or phone",
    searchHintNamesOnly: "Customer or contact name",
    listAria: "Conversation threads",
    unread: "{{count}} unread",
    // §4.1: the WA session-window countdown chip, shown only when < 4 h
    // remain. Time is the age.* short form.
    waWindow: "Window closes in {{time}}",
    noPreview: "No messages yet",
    unknownName: "Unknown sender",
    emptyTitle: "No conversations yet.",
    emptyBody: "Link your first customer from their contact page.",
    emptyCta: "Open customers",
    emptyUnassigned: "Inbox zero",
    emptyUnclaimed: "No unclaimed messages.",
    emptyFiltered: "No conversations match.",
    error: "Could not load the conversations",
  },

  // ---- §4.1 center pane — the thread ---------------------------------------
  thread: {
    pickOne: "Pick a conversation from the list.",
    pickOneBody: "Its messages and the customer's details open here.",
    error: "Could not load the thread",
    messagesError: "Could not load the messages",
    showEarlier: "Show earlier messages",
    backToList: "Back to the inbox",
    // Message status ticks (§2.7 rollup). Telegram is sent-terminal: the
    // delivered/read labels exist for the WA rows only.
    msgStatus: {
      received: "Received",
      queued: "Queued",
      sending: "Sending",
      sent: "Sent",
      delivered: "Delivered",
      read: "Read",
      failed_retryable: "Failed — will retry",
      failed_permanent: "Failed",
      suppressed: "Suppressed",
    },
    retry: "Retry",
    retryFailed: "Could not retry the message.",
    templateBadge: "Template: {{code}}",
    // Task #278: a failed message whose `status_detail` the house wrote is
    // said here in the reader's language. The first four are the send-time
    // guard's refusals before the provider call; the rest are the retry
    // sweeper's and the worker's own details (review of #278). Any other
    // detail is a provider's own words and is shown as the server sent it.
    // `provider` is the messenger's name.
    refused: {
      body_empty: "Not sent. {{provider}} would show this message as empty.",
      body_too_long:
        "Not sent. The text is {{length}} characters long, and {{provider}} takes at most 4096.",
      body_markup_invalid:
        "Not sent. Telegram cannot read the formatting of this template. Correct its body in the template catalog.",
      button_url_invalid:
        "Not sent. The link of button {{position}} does not open as an address. Correct it in the template catalog.",
      stale_bulk:
        "Not sent. It was a bulk message that waited in the queue for more than a day, so it was dropped.",
      fallback_rerouted:
        "Not sent on this messenger. It was sent again on the customer's other messenger.",
      fallback_superseded:
        "Not sent. It was a copy for the customer's other messenger, and the first message was settled before the copy went out.",
      worker_lost_mid_send:
        "The send stopped before {{provider}} answered. It will be tried again.",
      account_disconnected:
        "Not sent. The {{provider}} account for this message was disconnected before it went out.",
      account_suspended: "Not sent. The {{provider}} account for this message is suspended.",
      // D-329: a message with files.
      caption_too_long:
        "Not sent. The text is {{length}} characters long, and {{provider}} takes at most 1024 with attachments.",
      media_unavailable:
        "Not sent. An attached file is no longer stored, so there was nothing to send.",
      media_read_failed:
        "The attached files could not be read this time. The send will be tried again.",
      // D-585: a document that follows a quote's words whose words failed.
      lead_not_sent:
        "Not sent. This file follows a message that did not go out, so it was not sent on its own.",
    },
    mediaPending: "Fetching the attachment…",
    mediaExpired: "The attachment expired before it could be fetched. Ask the customer to resend.",
    mediaUnavailable: "This attachment cannot be shown",
    mediaOpen: "Open the attachment",
    // D-301: a sent total or tracking link is masked (•••) for a reader
    // without sales values; a body the server cannot re-render is withheld.
    valueWithheld: "Prices and links in this message are hidden from your role.",
    bodyWithheld:
      "This message holds prices or links your role does not see, so its text is hidden.",
    // D-337 ruling 5: an inbound message the customer forwarded from another
    // chat is withheld whole (text and files) from a reader without sales
    // values, because a forward is often our own quote or invoice sent back.
    forwardedWithheld:
      "The customer forwarded this message from another chat. Forwarded text and files are hidden from your role, because they often carry prices.",
    // D-585: the quote's accept link is masked for every reader, and a
    // priced document sent with a message is withheld from a reader without
    // sales values.
    credentialWithheld:
      "The accept link is hidden here, because anyone holding it can accept the offer for the customer. The customer received it in this message.",
    attachmentWithheld:
      "The document sent with this message prints prices, so it is hidden from your role.",
    sender: {
      contact: "Customer",
      user: "Staff",
      agent: "Agent",
      system: "Bot",
    },
  },

  // ---- §4.1 composer -------------------------------------------------------
  composer: {
    label: "Reply",
    placeholder: "Write a reply…",
    send: "Send",
    sending: "Sending…",
    sendFailed: "Could not send the message.",
    attach: "Attach a file",
    attachTooLarge: "Files over 25 MB cannot be sent.",
    // D-329: one message's files leave in one send.
    attachTotalTooLarge: "The files of one message can be at most 50 MB together.",
    attachLimit: "A message can carry at most 10 files.",
    captionTooLong:
      "With attachments the text can be at most 1024 characters. It is {{length}} now.",
    attachFailed: "Could not upload the attachment.",
    removeAttachment: "Remove the attachment",
    // §4.1: when the WA 24 h window is closed the free-text box is REPLACED
    // by the template picker with this chip.
    sessionClosed: "Session closed — pick an approved template",
    templateOpen: "Insert template",
    templateTitle: "Send a template",
    templateSearch: "Search templates",
    templateEmpty: "No sendable template for this thread.",
    templateSend: "Send template",
    templateVarRequired: "Required",
  },

  // ---- email intake in the inbox (P3-E2 PR E2-2e, D-327) -------------------
  // An email thread (§4.1), the email triage card (§4.2, spec 09 §4.11) and
  // the Unverified mail list (§4.2). Email is inbound only, so there is no
  // composer on an email thread. A sender is an address: a reader without
  // `messaging.address.read` is served it masked and is served no display
  // name (D-327 rulings 13 and 20), and these lines paint what was served.
  email: {
    // A sender a holder of the address grant reads: the From display name
    // and the address. Pure interpolation, the same in every language.
    senderNamed: "{{name}} <{{address}}>",
    noSubject: "No subject",
    // D-327 ruling 6: DMARC proves the domain, never the mailbox, so the
    // mark names the domain.
    verified: "Sender domain verified",
    notVerified: "Sender not verified",
    releasedBy: "Released by {{name}}",
    truncated: "Text cut at 64 KB",
    // D-327 ruling 12: the quoted or forwarded part under a reply, served
    // only to a holder of `sales.read_value`, because it usually holds our
    // own earlier mail and its prices.
    earlier: "Earlier message",
    earlierHidden: "Earlier message hidden",
    // D-337 ruling 5 on email (decided in E2-2e's review): a forward's
    // subject and files are the forwarded message's, hidden from a reader
    // without sales values; the customer's own words above it stay.
    forwardWithheld:
      "The customer forwarded another message here. Its subject and files are hidden from your role, because they often carry prices.",
    filesAria: "Files of this mail",
    fileUnnamed: "Unnamed file",
    download: "Download",
    downloading: "Preparing the download…",
    downloadFailed: "Could not download the file.",
    heldWaiting: "Waiting until the sender is linked",
    // Why a part was neither stored nor held (spec 17 §2.14 omitted_parts).
    omitted: {
      virus_not_clean: "Not scanned clean, so it was not kept",
      spam: "The mail was marked as spam, so it was not kept",
      too_large: "Larger than 25 MB, so it was not kept",
      type_not_allowed: "A type we do not accept, so it was not kept",
      bytes_mismatch: "Its contents do not match its type, so it was not kept",
      expired_before_linking: "Expired before the sender was linked",
    },
    // D-327 ruling 9: the office does not reply by email; the seller replies
    // from their own mail app.
    inboundOnly: "Reply to this email from your own mail app.",
    // The triage card at the head of an unclaimed email thread (§4.2).
    triageTitle: "Mail from a sender no contact holds",
    channel: "Channel",
    subject: "Subject",
    sender: "Sender",
    // D-327 ruling 7: a display name holding `@` can pose as another
    // address, so the reader is shown the address that really sent it.
    nameHoldsAddress: "The name contains an address. The sender is {{address}}",
    held: {
      intro: "Mail from known customers whose domain didn't verify waits here.",
      listAria: "Unverified mail",
      received: "Received {{time}}",
      openThread: "Open the conversation",
      noCustomer: "Not a known contact yet",
      release: "Release",
      releasing: "Releasing…",
      discard: "Discard",
      discarding: "Discarding…",
      releaseTitle: "Release this mail?",
      releaseBody:
        "The mail joins the sender's conversation marked “Sender not verified”. If the sender is not a known contact, it goes to Unclaimed instead.",
      chooseMailbox: "Mailbox that sent it",
      chooseMailboxHint:
        "The From line names several mailboxes. Choose the one you vouch for, and the mail is filed under it.",
      mailboxInvalid: "Choose the mailbox that sent this mail.",
      noMailbox: "The sender could not be read, so this mail can only be discarded.",
      discardTitle: "Discard this mail?",
      discardBody: "The text of this mail is deleted and cannot come back.",
      releaseFailed: "Could not release the mail.",
      discardFailed: "Could not discard the mail.",
      decidedElsewhere: "Someone else has already decided this mail, so the list was refreshed.",
      offline: "You are offline. Release and Discard come back with the network.",
      empty: "No held mail",
      error: "Could not load the unverified mail",
    },
  },

  // ---- §4.1 right pane — context; §4.2 claim -------------------------------
  context: {
    aria: "Conversation context",
    open: "Details",
    customerTitle: "Customer",
    noCustomer: "No customer linked yet.",
    openCustomer: "Open the customer",
    // §4.2 — attach an unclaimed thread to a Module 09 contact.
    attachTitle: "Attach to a customer",
    attachSearch: "Search contacts",
    attachHint: "Contact or customer name",
    attachNone: "No contact matches.",
    attachConfirm: "Attach",
    attachFailed: "Could not attach the conversation.",
    markSpam: "Mark spam",
    spamTitle: "Mark as spam?",
    spamBody: "This closes the conversation and revokes the sender's identity.",
    spamFailed: "Could not mark this as spam.",
    ordersTitle: "Linked orders",
    primaryChip: "Primary",
    primaryOrderOpen: "Open the order",
    // The linked-orders list (§4.1). It is the customer's most recent orders,
    // read from the order book — no money on this pane: the reader here is
    // answering a message, and the list's job is "which order is this about".
    ordersError: "Could not load the orders.",
    ordersEmpty: "No orders for this customer yet.",
    ordersHidden: "Orders are shown to the sales team and the admins.",
    setPrimaryOrder: "Set as primary",
    setPrimaryFailed: "Could not set the primary order.",
    identityTitle: "Identity",
    language: "Language",
    verifiedAt: "Verified {{date}}",
    pendingIdentity: "Not verified yet",
    optedOut: "Opted out of marketing",
    optedOutHard: "Opted out of everything",
    assignLabel: "Assign",
    assignNone: "Unassigned",
    assignMe: "Assign to me",
    assignFailed: "Could not assign the conversation.",
    snoozeLabel: "Snooze",
    snooze2h: "2 hours",
    snoozeTomorrow: "Tomorrow",
    snoozeNextWeek: "Next week",
    snoozedUntil: "Snoozed until {{date}}",
    snoozeFailed: "Could not snooze the conversation.",
    closeThread: "Close conversation",
    reopenThread: "Reopen",
    closeFailed: "Could not update the conversation.",
    // §4.1's "Create quote" door: the composer opens already pointed at this
    // conversation's customer. Offered only to a manager who may write a
    // quote, and only once the conversation has a customer to write it for.
    createQuote: "Create quote",
    createQuoteNeedsCustomer: "Link this conversation to a customer to draft a quote for them.",
    // The Create deal door beside Create quote (spec 17 §4.1, spec 09 §4.14,
    // D-312): offered to a holder of `sales.opportunity.write` on a thread
    // with a customer, it opens New deal on the board with the customer locked
    // and this conversation as the deal's source. It prints no count, because
    // no read serves one. A thread with no customer reads the link-first
    // sentence above.
    createDeal: "Create deal",
  },

  // ---- §4.3 template catalog ----------------------------------------------
  templates: {
    new: "New template",
    createTitle: "New template",
    code: "Code",
    codeHint: "snake_case, e.g. order_status_ready",
    name: "Name",
    category: "Category",
    categories: {
      utility: "Utility",
      marketing: "Marketing",
      authentication: "Authentication",
    },
    colCode: "Code",
    colName: "Name",
    colCategory: "Category",
    colVariants: "Variants",
    systemChip: "System",
    systemHint: "Seeded template — it cannot be deleted or deactivated.",
    inactiveChip: "Inactive",
    tableCaption: "Message templates",
    // Task #278: the catalog opens on the active templates, 25 to a page, and
    // shows the deactivated ones in a second list only when asked.
    showInactive: "Show deactivated templates",
    inactiveTitle: "Deactivated templates",
    inactiveCaption: "Deactivated message templates",
    inactiveEmpty: "No template is deactivated.",
    // §4.3 says this state is impossible (system templates are ensured on
    // first touch); the screen still says something true if it ever shows.
    emptyTitle: "No templates",
    emptyBody: "System templates are seeded on first use.",
    error: "Could not load the templates",
    createFailed: "Could not create the template.",
    updateFailed: "Could not save the template.",
    readOnly: "Templates are edited by sales and the admins.",
    activeLabel: "Active",
    // The per-provider × locale matrix chips. Telegram variants are sendable
    // immediately (`not_required`); WA cells render their Meta status. The
    // chip's accessible name composes provider + locale from data in code —
    // there is no word in it to translate.
    sendable: "Sendable",
    noVariant: "No variant",
    metaStatus: {
      not_required: "Sendable",
      draft: "Draft",
      submitted: "Submitted",
      approved: "Approved",
      rejected: "Rejected",
      paused: "Paused",
      disabled: "Disabled",
    },
    // Meta's own rejection text rides verbatim behind this label (D-022).
    rejectedReason: "Meta said: {{reason}}",
    editorTitle: "Variant editor",
    body: "Body",
    headerText: "Header",
    footerText: "Footer",
    buttonsTitle: "Buttons",
    addButton: "Add button",
    removeButton: "Remove button",
    buttonKind: "Type",
    buttonQuickReply: "Quick reply",
    buttonUrl: "Link",
    buttonLabel: "Label",
    buttonUrlField: "URL",
    counter: "{{used}} / {{max}}",
    insertVariable: "Insert {{key}}",
    preview: "Preview",
    previewEmpty: "Write a body to preview it.",
    saveVariant: "Save variant",
    variantFailed: "Could not save the variant.",
    // §4.3's "Submit to Meta" renders PARKED-DISABLED with the reason while
    // D-232 stands — a visible reason, never a dead button doing nothing.
    submitMeta: "Submit to Meta",
    submitParked: "You can send templates to Meta once WhatsApp is connected.",
    variablesTitle: "Variables",
    varKey: "Key",
    varSample: "Sample",
    varRequired: "Required",
    addVariable: "Add variable",
    removeVariable: "Remove variable",
    variablesHint: "Samples fill the preview and Meta's submission examples.",
  },

  // ---- the server's field refusals, by their `errors[].message_key` -------
  // Task #238: the variant PUT refuses a Telegram body outside spec 17 §2.5's
  // HTML subset with one entry per fault, keyed
  // `messaging.validation.telegram_html.{code}` and carrying `token` (the
  // offending tag or character, verbatim) and `offset`. The editor turns the
  // offset into `position` (1-based, the character a person counts to) and
  // renders each line under the body. The tags and entities quoted in these
  // sentences are Telegram's own syntax and read the same in every language.
  validation: {
    telegram_html: {
      tag_not_allowed:
        '{{token}} at character {{position}} is not a tag Telegram accepts. Use <b>, <i>, <code> or <a href="…">, or write the brackets as &lt; and &gt;.',
      bracket_not_escaped:
        "The {{token}} at character {{position}} is not part of a tag. Write it as &lt; or &gt;.",
      ampersand_not_escaped:
        "{{token}} at character {{position}} is not an entity Telegram accepts. Write a plain & as &amp;.",
      tag_not_closed: "{{token}} at character {{position}} is never closed.",
      close_without_open:
        "{{token}} at character {{position}} closes a tag that is not open, or closes it out of order.",
      attributes_not_allowed:
        "{{token}} at character {{position}} carries attributes. Only <a> takes one, href.",
      link_needs_href: '{{token}} at character {{position}} needs exactly one href="…".',
      link_scheme_not_allowed:
        "The link {{token}} at character {{position}} must start with https://, http://, tg:// or a variable.",
      tag_space_not_plain:
        "{{token}} at character {{position}} has a special space Telegram can't read in a tag. Retype it as a normal space.",
      body_empty: "The body has no visible text, and Telegram does not send an empty message.",
      // A code this build does not know yet still says where the fault is.
      fallback: "Telegram cannot read the body at character {{position}}.",
      show: "Show",
    },
    // Task #278: the variant PUT renders a url button's link with the
    // template's samples, and a template PATCH re-renders them with the new
    // variables. `label` is the button's label, `token` what the link came
    // out as, or the placeholder no variable declares.
    button_url: {
      url_variable_unknown:
        "The link of “{{label}}” uses {{token}}, which isn't a variable here. Add it to the variables or remove it.",
      url_variable_optional:
        "The link of “{{label}}” needs {{token}}, or it opens nothing when left blank. Make the variable required.",
      url_invalid:
        "The link of the button “{{label}}” does not come out as an address a customer can open: {{token}}",
      url_scheme_not_allowed:
        "The link of the button “{{label}}” must start with https://, or tg:// on Telegram: {{token}}",
      fallback: "The link of the button “{{label}}” cannot be sent.",
    },
  },

  // ---- §4.4 messaging settings --------------------------------------------
  settings: {
    error: "Could not load the settings",
    accountStatus: {
      connecting: "Connecting",
      active: "Active",
      webhook_failing: "Webhook failing",
      suspended: "Suspended",
      disconnected: "Disconnected",
    },
    lastWebhook: "Last webhook {{time}}",
    neverWebhook: "No webhook received yet",
    botUsername: "Bot",
    reconnect: "Reconnect",
    probing: "Probing…",
    probeOk: "Webhook verified.",
    probeNotOk: "The webhook is not receiving.",
    // §4.4: the provider's own last-error text, verbatim, as a typed field.
    providerDetail: "Provider detail: {{text}}",
    probeFailed: "Could not probe the connection.",
    disconnect: "Disconnect",
    disconnectTitle: "Disconnect Telegram?",
    disconnectBody: "Customers cannot reach you on this bot until it is reconnected.",
    disconnecting: "Disconnecting…",
    disconnectFailed: "Could not disconnect.",
    notConnected: "Telegram is not connected yet.",
    connectTitle: "Connect Telegram",
    connectToken: "Bot token",
    connectTokenHint: "Paste the token from @BotFather",
    connectSubmit: "Connect",
    connecting: "Connecting…",
    connectFailed: "Could not connect the bot.",
    copyRequestId: "Copy request ID",
    // The D-232 parked card (§3.1, §4.4).
    waParkedTitle: "WhatsApp opens after Meta's checks",
    waParkedBody:
      "Assessly is finishing Meta's checks for WhatsApp. Until then, customers reach you on Telegram.",
    waSignupDisabled: "You can connect WhatsApp here once it is ready.",
    healthTitle: "Delivery health",
    healthState: {
      healthy: "Healthy",
      degraded: "Degraded",
      down: "Down",
    },
    queuedCount: "Queued: {{count}}",
    oldestQueued: "Oldest queued {{time}}",
    errorRate: "Error rate {{pct}}",
    circuitOpened: "Circuit opened {{time}}",
    healthEmpty: "No provider connected yet.",
    logTitle: "Webhook log",
    logCaption: "Last 50 webhook events, newest first",
    colTime: "Time",
    colKind: "Kind",
    colStatus: "Status",
    processing: {
      pending: "Pending",
      processed: "Processed",
      skipped: "Skipped",
      error: "Error",
    },
    // The log row's KIND — the wire classification named in words (the S7
    // raw-enum-in-a-cell defect, not re-shipped here).
    webhookKind: {
      message: "Message",
      callback_query: "Button tap",
      my_chat_member: "Membership change",
      other: "Other",
    },
    viewPayload: "View payload",
    payloadTitle: "Webhook payload",
    logEmpty: "No webhook events yet.",
    logError: "Could not load the webhook log",
    // The Email intake card (§4.4, D-327 rulings 5 and 19): one fixed address
    // per tenant, inbound only, and the receiving rule set's state as this
    // tenant last checked it or last received mail.
    email: {
      title: "Email intake",
      offBody:
        "Turn intake on to get customers' mail at one address. Mail from known customers joins their conversations.",
      turnOn: "Turn on",
      turningOn: "Turning on…",
      turnOff: "Turn off",
      turningOff: "Turning off…",
      address: "Address",
      copy: "Copy address",
      forwardLine:
        "You can forward your sales mailbox here. Forwarded mail waits in Unverified mail unless the customer's domain verifies.",
      turnOffTitle: "Turn off email intake?",
      turnOffBody:
        "Mail sent to this address while intake is off is not received. The address stays the same when you turn intake on again.",
      unavailable: "Email intake isn't available in this workspace.",
      enableFailed: "Could not turn email intake on.",
      disableFailed: "Could not turn email intake off.",
      intakeOn: "Intake on",
      intakeOff: "Intake off",
      ruleSetActive: "Receiving is active",
      ruleSetInactive: "Receiving is not active",
      ruleSetUnknown: "Receiving could not be checked",
      checkedAt: "Checked {{time}}",
      lastMail: "Last mail {{time}}",
      noMail: "No mail received yet",
      probe: "Check receiving",
      probing: "Checking…",
      probeOk: "Mail is being received.",
      probeInactive: "Mail is not being received. The receiving rule set is not active",
      probeUnreachable: "Receiving could not be checked. Try again in a few minutes.",
      probeFailed: "Could not check receiving.",
      readOnly: "The group and facility admins turn email intake on and off.",
      healthMissing: "Receiving has not been checked yet.",
      error: "Could not load the email intake",
      offline: "You are offline. The switch and the check come back with the network.",
    },
  },

  // ---- §4.5 identity link widget (embedded in the customer 360) ------------
  link: {
    title: "Messaging",
    aria: "Messaging identities",
    notLinked: "Not linked",
    qrUnavailable: "The link cannot be drawn as a QR code; use the link or the code.",
    linkedChip: "Linked",
    pendingChip: "Pending",
    verifiedAt: "Verified {{date}}",
    optedOut: "Opted out of marketing",
    optedOutHard: "Opted out of everything",
    generate: "Generate link code",
    generating: "Generating…",
    generateFailed: "Could not create the link code.",
    modalTitle: "Telegram link code",
    codeOnce: "Shown once — copy it now.",
    codeAria: "Link code",
    deepLink: "Open in Telegram",
    qrAria: "QR code for the Telegram link",
    copyCode: "Copy code",
    expiresIn: "Expires in {{time}}",
    expired: "This code has expired. Generate a new one.",
    revoke: "Revoke",
    revokeTitle: "Revoke this identity?",
    revokeBody: "The customer stops receiving messages on this channel until re-linked.",
    revokeReason: "Reason",
    revokeConfirm: "Revoke",
    revokeFailed: "Could not revoke the identity.",
    waParked: "WhatsApp opens after Meta's checks. Link Telegram meanwhile.",
    error: "Could not load the messaging identities",
  },

  // ---- the offer agent in a thread (D-515 ruling 4) ----------------------
  agent: {
    stripAria: "Offer agent on this conversation",
    draftOffer: "Draft offer",
    starting: "Starting…",
    draftChip: "Agent's draft",
    loading: "Checking the offer agent…",
    error: "Could not read the offer agent's run.",
  },
} as const;

// The dictionary SHAPE, with values widened to `string` (src/i18n-shape.ts).
export type MessagingDict = Translated<typeof messagingEn>;

// ru is TRANSLATED (see messaging.ru.ts). fr, zh, zh-Hant and hi are
// machine-translated in their own files (messaging.<locale>.ts), which
// src/locales/<locale>.ts imports so each stays in its locale's chunk. The
// claim is declared in src/i18n-completeness.ts and enforced by
// tools/ci/i18n-parity.ts, which fails if a translated value regresses to en.
export const messagingRu: MessagingDict = messagingRuDict;
